/**
 * usePOSCart - Custom hook for POS cart management
 * Handles add, update, remove, clear operations with haptic feedback
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';

export function usePOSCart(initialCart = []) {
  const [cart, setCart] = useState(initialCart);

  // Haptic feedback helper
  const vibrate = useCallback((pattern) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  }, []);

  // Add item to cart
  const addToCart = useCallback((item, customizations = null, selectedSize = null, isCoffeePass = false) => {
    // Use BASE item.price as default - sizes are upsells, only apply if explicitly selected
    const itemPrice = selectedSize?.price || item.price || 0;
    const customizationsTotal = customizations?.reduce((sum, c) => sum + (c.price || 0), 0) || 0;
    const totalPrice = itemPrice + customizationsTotal;
    
    // Special cart item structure for Coffee Pass (both Standard and Extended)
    if (isCoffeePass || item.is_coffee_pass) {
      setCart(prev => {
        const existingPasses = prev.filter(c => c.isCoffeePass);
        const passNumber = existingPasses.length + 1;
        
        if (passNumber > 1) {
          toast.info('Multiple passes added! Extra passes can be gifted.', {
            description: 'The first pass is for the customer. Additional passes create gift codes.',
            duration: 4000
          });
        }
        
        // Use the actual price from the menu item (default to $95 for Extended Pass)
        const passPrice = item.price || 95;
        const planType = item.plan_type || 'standard';
        const isExtended = planType === 'extended' || item.id === 'bula-coffee-pass-extended';
        const passName = isExtended ? 'Extended Bula Coffee Pass' : 'Bula Coffee Pass';
        
        const coffeePassItem = {
          item_id: item.id || `bula-coffee-pass-${Date.now()}`,
          name: passName,
          price: passPrice,
          quantity: 1,
          customizations: [],
          size: null,
          image: item.image || null,
          category: 'membership',
          isCoffeePass: true,
          is_coffee_pass: true,
          plan_type: planType,
          product_id: item.id,
          pass_number: passNumber
        };
        
        vibrate(50);
        toast.success(`${passName} added! ($${passPrice})`, { duration: 2000 });
        return [...prev, coffeePassItem];
      });
      return;
    }
    
    // Generate unique cart item ID based on item + size + price
    // This ensures same item with different sizes are SEPARATE line items
    const sizeKey = selectedSize ? `-${selectedSize.name || selectedSize.id}-${totalPrice}` : `-base-${totalPrice}`;
    const customKey = customizations && customizations.length > 0 ? `-${Date.now()}` : '';
    const cartItemId = `${item.id}${sizeKey}${customKey}`;
    
    const cartItem = {
      item_id: cartItemId,
      name: item.name + (selectedSize ? ` (${selectedSize.name})` : ''),
      price: totalPrice,
      quantity: 1,
      customizations: customizations || [],
      size: selectedSize,
      image: item.image,
      category: item.category
    };
    
    setCart(prev => {
      // Only merge if EXACT same item (same ID, size, price) and no customizations
      const existing = prev.find(c => c.item_id === cartItem.item_id && !customizations && c.customizations?.length === 0);
      if (existing && !customizations) {
        return prev.map(c => c.item_id === cartItem.item_id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      // Different size/price = new line item
      return [...prev, cartItem];
    });
    
    // Haptic feedback for mobile devices
    vibrate(50);
    toast.success(`Added ${item.name}`, { duration: 1500 });
  }, [vibrate]);

  // Update item quantity
  const updateQuantity = useCallback((itemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.item_id === itemId) {
        const newQty = item.quantity + delta;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
    
    vibrate(25);
  }, [vibrate]);

  // Remove item from cart
  const removeItem = useCallback((itemId) => {
    setCart(prev => prev.filter(item => item.item_id !== itemId));
    vibrate([25, 50, 25]);
  }, [vibrate]);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // Calculate cart totals
  const calculateTotals = useCallback((discountPercent = 0, dollarDiscount = 0) => {
    const subtotal = cart.reduce((sum, item) => {
      const itemPrice = item.price || 0;
      const addOnsTotal = (item.add_ons || []).reduce((aSum, a) => aSum + (a.price || 0) * (a.quantity || 1), 0);
      return sum + ((itemPrice + addOnsTotal) * (item.quantity || 1));
    }, 0);
    
    // Apply percentage discount
    let discountAmount = subtotal * (discountPercent / 100);
    // Apply dollar discount
    discountAmount += dollarDiscount;
    
    const taxRate = 0.125;
    const afterDiscount = Math.max(0, subtotal - discountAmount);
    const taxAmount = afterDiscount * taxRate / (1 + taxRate);
    const total = afterDiscount;
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
      itemCount: cart.reduce((sum, item) => sum + (item.quantity || 1), 0)
    };
  }, [cart]);

  // Restore cart from saved data (for parked sales)
  const restoreCart = useCallback((savedCart) => {
    const restoredCart = (savedCart || []).map(item => ({
      id: item.item_id || item.id || `item-${Date.now()}`,
      item_id: item.item_id,
      name: item.name,
      price: item.price,
      quantity: item.quantity || 1,
      category: item.category,
      size: item.size,
      selectedSize: item.selectedSize,
      add_ons: item.add_ons || [],
      special_instructions: item.special_instructions,
      is_coffee_pass_redemption: item.is_coffee_pass_redemption || false,
      customizations: item.customizations || []
    }));
    setCart(restoredCart);
  }, []);

  return {
    cart,
    setCart,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    calculateTotals,
    restoreCart,
    isEmpty: cart.length === 0,
    itemCount: cart.reduce((sum, item) => sum + (item.quantity || 1), 0)
  };
}

export default usePOSCart;
